import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Video, Clock, Calendar as CalendarIcon, Sparkles, Ticket, Copy } from "lucide-react";
import { toast } from "sonner";
import DeleteAccountCard from "@/components/DeleteAccountCard";
import RescheduleControls from "@/components/RescheduleControls";
import { UsernamePrompt } from "@/components/UsernamePrompt";
import FeedbackViewer from "@/components/FeedbackViewer";

function StatusBadge({ b }) {
  const map = {
    pending_payment: { label: "Awaiting payment", color: "#E8B05C" },
    confirmed: { label: "Confirmed", color: "var(--brand)" },
    completed: { label: "Completed", color: "#638496" },
    cancelled: { label: "Cancelled", color: "#E6B23A" },
  };
  const s = map[b.status] || map.confirmed;
  return (
    <span className="chip" style={{ background: `${s.color}20`, color: s.color }}>{s.label}</span>
  );
}

export default function ClientDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [myApp, setMyApp] = useState(null);
  const [myCoupons, setMyCoupons] = useState([]);
  const promotedRef = useRef(false);

  const loadApp = () => {
    api.get("/counselor-applications/mine")
      .then(r => setMyApp(r.data))
      .catch(err => {
        if (err?.response?.status && err.response.status !== 404) {
          console.warn("Failed to load application status:", err?.message || err);
        }
      });
  };

  useEffect(() => {
    api.get("/bookings/mine").then(r => setBookings(r.data));
    api.get("/coupons/mine").then(r => setMyCoupons(r.data)).catch(() => {});
    loadApp();
  }, []);

  // Keep bookings fresh so the auto-join banner reflects newly-confirmed sessions
  // even after long idle periods (e.g. a client leaves the dashboard tab open).
  useEffect(() => {
    const iv = setInterval(() => {
      api.get("/bookings/mine")
        .then(r => setBookings(r.data))
        .catch(err => {
          if (err?.response?.status && err.response.status !== 401) {
            console.warn("Bookings refresh failed:", err?.message || err);
          }
        });
    }, 60_000);
    return () => clearInterval(iv);
  }, []);

  // Poll for approval — the moment admin approves the application, the user
  // is promoted to role=counselor server-side. As soon as we detect it we
  // congratulate them and drop them straight into the membership checkout,
  // no sign-out needed.
  useEffect(() => {
    if (!myApp || myApp.status !== "pending") return;
    const iv = setInterval(async () => {
      loadApp();
      const fresh = await refresh();
      if (fresh?.role === "counselor" && !promotedRef.current) {
        promotedRef.current = true;
        clearInterval(iv);
        toast.success("🎉 You're approved! Welcome to the team.", { duration: 6000 });
        setTimeout(() => nav("/counselor"), 900);
      }
    }, 15000);
    return () => clearInterval(iv);
  }, [myApp, refresh, nav]);

  // If the user is on /dashboard but is already a counselor (edge case:
  // admin approved while they were on this exact page), send them home.
  useEffect(() => {
    if (user?.role === "counselor" && !promotedRef.current) {
      promotedRef.current = true;
      nav("/counselor");
    }
    if (user?.role === "employee" && !promotedRef.current) {
      promotedRef.current = true;
      nav("/employee");
    }
  }, [user, nav]);

  const upcoming = bookings.filter(b => ["confirmed","pending_payment"].includes(b.status));
  const past = bookings.filter(b => ["completed","cancelled"].includes(b.status));

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {user && !user.username && <UsernamePrompt onSet={refresh} />}
      <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
        <div>
          <span className="label-eyebrow">Dashboard</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-2">Hello, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Link to="/book" className="btn-outline" data-testid="dash-new-booking">Book a session</Link>
      </div>

      {myCoupons.length > 0 && (
        <section className="mb-10">
          <h2 className="font-serif-display text-2xl mb-4">Your coupons</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="my-coupons-list">
            {myCoupons.map(c => (
              <div key={c.id} className="card-soft p-5 flex items-center justify-between gap-3" data-testid={`my-coupon-${c.id}`}>
                <div className="min-w-0" style={c.expired ? { opacity: 0.55 } : undefined}>
                  <div className="inline-flex items-center gap-1.5 text-xs" style={{color: c.expired ? "var(--text-secondary)" : "var(--brand)"}}>
                    <Ticket className="w-3.5 h-3.5"/> {c.discount_percent}% off
                  </div>
                  <div className="font-serif-display text-lg mt-0.5">{c.service_name}</div>
                  <div className="text-xs mt-1 font-mono tracking-wide" style={{color:"var(--text-secondary)"}}>{c.code}</div>
                  {!c.expired && c.expires_at && (
                    <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
                      Valid until {new Date(c.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                {c.expired ? (
                  <span className="chip text-xs flex-shrink-0" style={{background:"#b91c1c", color:"#fff"}}>Expired</span>
                ) : (
                  <button data-testid={`copy-my-coupon-${c.id}`}
                    onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied — use it at checkout"); }}
                    className="btn-outline text-xs inline-flex items-center gap-1 flex-shrink-0">
                    <Copy className="w-3.5 h-3.5"/> Copy
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {myApp && (
        <section className="mb-10">
          <div className="card-soft p-6" data-testid="my-application-card">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
              <div>
                <span className="label-eyebrow">Counselor application</span>
                <div className="font-serif-display text-2xl mt-1">Your application</div>
              </div>
              <span className="chip text-[0.65rem]" style={{
                background: myApp.status === "approved" ? "rgba(91,117,83,0.15)"
                          : myApp.status === "rejected" ? "rgba(230,178,58,0.15)" : "rgba(232,176,92,0.2)",
                color: myApp.status === "approved" ? "var(--brand)"
                     : myApp.status === "rejected" ? "var(--accent)" : "#8a6a1f"
              }}>{myApp.status.replace("_", " ")}</span>
            </div>

            {myApp.status === "pending" && (
              <p className="text-sm" style={{color:"var(--text-secondary)"}}>
                We've received your application. Our team reviews personally — you'll see the next step here within 3–5 working days.
              </p>
            )}
            {myApp.status === "rejected" && (
              <p className="text-sm" style={{color:"var(--text-secondary)"}}>
                Unfortunately your application wasn't accepted this time. You're welcome to reapply — <Link to="/apply" className="underline" style={{color:"var(--brand)"}}>update and resubmit</Link>.
              </p>
            )}
            {myApp.status === "approved" && (
              <div className="rounded-2xl p-5 mt-1" style={{background:"rgba(91,117,83,0.08)", border:"1px solid rgba(91,117,83,0.25)"}} data-testid="approved-celebration">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 flex-shrink-0" style={{color:"var(--brand)"}}/>
                  <div className="flex-1">
                    <div className="font-serif-display text-xl" style={{color:"var(--brand)"}}>🎉 Congratulations — you're approved!</div>
                    <p className="text-sm mt-1" style={{color:"var(--text-primary)"}}>
                      Welcome to the team. Your counselor dashboard is ready — just complete the membership payment and you'll be live for bookings.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link data-testid="go-to-membership" to="/counselor/membership" className="btn-primary text-sm">
                        Activate membership
                      </Link>
                      <Link to="/counselor" className="btn-outline text-sm">
                        Go to counselor dashboard
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="mb-14">
        <h2 className="font-serif-display text-2xl mb-4">Upcoming</h2>
        {upcoming.length === 0 && <p style={{color:"var(--text-secondary)"}}>Nothing scheduled yet.</p>}
        <div className="grid md:grid-cols-2 gap-4" data-testid="upcoming-list">
          {upcoming.map(b => (
            <div key={b.id} className="card-soft p-6" data-testid={`booking-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-serif-display text-2xl">{b.service_name}</div>
                  <div className="text-sm mt-1" style={{color:"var(--text-secondary)"}}>
                    <CalendarIcon className="inline w-4 h-4 mr-1"/>{b.date} · <Clock className="inline w-4 h-4 mx-1"/>{b.start_time}–{b.end_time}
                  </div>
                  {b.counselor_name && (
                    <div className="text-xs mt-1" style={{color:"var(--brand)"}}>with {b.counselor_name}</div>
                  )}
                  {b.booked_for === "guardian" && b.student_name && (
                    <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
                      For {b.student_name}{b.student_age ? ` (age ${b.student_age})` : ""}
                    </div>
                  )}
                </div>
                <StatusBadge b={b}/>
              </div>
              <div className="mt-5 flex gap-2 items-center flex-wrap">
                {b.status === "confirmed" ? (
                  <RescheduleControls booking={b} viewerRole="client"
                    testIdPrefix={`client-b-${b.id}-`}
                    onChange={(updated) => setBookings(prev => prev.map(x => x.id === updated.id ? updated : x))}/>
                ) : b.status === "pending_payment" ? (
                  <span className="text-sm" style={{color:"var(--text-secondary)"}}>
                    Payment pending — <Link to="/book" className="underline" style={{color:"var(--brand)"}}>rebook</Link> to complete secure payment.
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif-display text-2xl mb-4">Past sessions</h2>
        {past.length === 0 && <p style={{color:"var(--text-secondary)"}}>No past sessions yet.</p>}
        <div className="grid md:grid-cols-2 gap-4">
          {past.map(b => (
            <div key={b.id} className="card-soft p-6 opacity-90" data-testid={`past-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-serif-display text-xl">{b.service_name}</div>
                  <div className="text-sm mt-1" style={{color:"var(--text-secondary)"}}>
                    {b.date} · {b.start_time}
                  </div>
                </div>
                <StatusBadge b={b}/>
              </div>
              {b.status === "completed" && (
                <div className="mt-3">
                  {b.feedback_submitted ? (
                    <FeedbackViewer bookingId={b.id} testIdPrefix="client-feedback" />
                  ) : (
                    <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      Feedback pending — you'll be prompted shortly.
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <DeleteAccountCard/>
    </div>
  );
}
